/**
 * WHAT THE HOMES UNDER A PLACE MAP HOLD, AND HOW EACH COUNT OVER THEM READS.
 *
 * PlaceSubdivisionHomes (a client component) draws this; it lives here so the
 * rule is pinned by tests without a browser. The map decides what the dials
 * hold: the subdivision chosen on the map, then the map's own type toggles
 * and price ceiling (W1, Muse site audit 2026-10-20: "after filtering, the map
 * legend updates but the dial header does not"). Every count over the dials
 * is read from what they now hold, through lib/listing-counts, so the block's
 * header and each dial's header move with the map's key.
 *
 * Client-safe: pure functions over plain rows.
 */
import { atlasFilterIsActive, atlasFilterKey, atlasFilterRows, type AtlasFilterState } from '@/lib/atlas/atlas-filter'
import { CHOSEN_TYPES_BASIS, placeStockListingCount } from '@/lib/listing-counts'
import { placeHomesCountLabel } from '@/lib/place/place-count-label'
import {
  PLACE_BUYER_GROUP_HEADING,
  PLACE_BUYER_GROUPS,
  placeBuyerGroup,
  type PlaceBuyerGroup,
} from '@/lib/place/place-type-style'

export type PlaceHomeRow = {
  listingKey: string
  propertyType: string | null
  propertySubType: string | null
  price: number | null
  standardStatus?: string | null
}

export type PlaceHomesSection<R extends PlaceHomeRow> = {
  key: PlaceBuyerGroup
  heading: string
  rows: R[]
  /** The dial's header count ("193 for sale", or "193 for sale, houses" with a basis). */
  countLabel: string | null
}

export function homesByBuyerGroup<R extends PlaceHomeRow>(listings: readonly R[]): Array<{
  key: PlaceBuyerGroup
  heading: string
  rows: R[]
}> {
  const buckets: Record<PlaceBuyerGroup, R[]> = {
    homes: [],
    cabins: [],
    attached: [],
    multifamily: [],
    lots: [],
    other: [],
  }
  for (const listing of listings) {
    buckets[placeBuyerGroup(listing.propertyType, listing.propertySubType)].push(listing)
  }
  return PLACE_BUYER_GROUPS.flatMap((key) => {
    const rows = buckets[key]
    if (rows.length === 0) return []
    return [{ key, heading: PLACE_BUYER_GROUP_HEADING[key], rows }]
  })
}

export function placeHomesView<R extends PlaceHomeRow>(input: {
  homes: readonly R[]
  keysBySlug: Readonly<Record<string, readonly string[]>>
  selectedId: string | null
  mapFilter: AtlasFilterState | null
  /** Whole-place label while `homes` is still a seed page (SITE-193). */
  homesCountLabel: string | null
  countScope?: string | null
  /** W1: every count names its basis in plain words. */
  countBasis?: boolean
}): {
  visible: readonly R[]
  countLabel: string | null
  sections: PlaceHomesSection<R>[]
  /** Re-keys every dial when the selection or the map's filter changes. */
  dialKey: string
} {
  const { homes, keysBySlug, selectedId, mapFilter, countBasis = false } = input
  const filtered = atlasFilterIsActive(mapFilter)
  const inSelection = selectedId
    ? (() => {
        const keys = new Set(keysBySlug[selectedId] ?? [])
        return homes.filter((home) => keys.has(home.listingKey))
      })()
    : homes
  const visible = atlasFilterRows(inSelection, mapFilter)
  // The seed page's whole-place label counts the place unfiltered, so a
  // selection or a filtered map drops it for the rows in hand.
  const countLabel =
    !selectedId && !filtered && input.homesCountLabel
      ? input.homesCountLabel
      : placeHomesCountLabel(
          visible,
          selectedId ? null : input.countScope,
          // Filtered, the block names the types the map's key names, and is
          // never "all home types" when the reader narrowed them.
          countBasis
            ? placeStockListingCount(visible, null, filtered ? (mapFilter?.basis ?? CHOSEN_TYPES_BASIS) : null)
                .basis
            : null,
        )
  const sections = homesByBuyerGroup(visible).map((section) => ({
    ...section,
    countLabel: placeHomesCountLabel(
      section.rows,
      null,
      countBasis ? placeStockListingCount(section.rows, section).basis : null,
    ),
  }))
  return {
    visible,
    countLabel,
    sections,
    dialKey: `${selectedId ?? 'all'}:${atlasFilterKey(mapFilter)}`,
  }
}
